import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useRef, useState, useEffect } from "react";
import { Rnd } from "react-rnd";
import "./styles.css";
import { getAllChunks, clearChunks, getAllSegmentKeys, getSegmentMeta, } from "./db";
import StreamRecorder from "./StreamRecorder";
import { CONTROL_ICONS } from "../../assets/content/controlIcons";
const Recorder = ({ streams }) => {
    const [recording, setRecording] = useState(false);
    const [paused, setPaused] = useState(false);
    const frontRef = useRef(null);
    const backRef = useRef(null);
    const dirHandleRef = useRef(null);
    const [hasVideos, setHasVideos] = useState(false);
    const [expectedWidth, setExpectedWidth] = useState(45);
    useEffect(() => {
        const buttonCount = 1 + (recording ? 1 : 0) + (hasVideos && !recording ? 1 : 0);
        setExpectedWidth(buttonCount === 3 ? 115 : buttonCount === 2 ? 80 : 45);
    }, [recording, paused, hasVideos]);
    const [dimensions, setDimensions] = useState(() => ({
        x: typeof window !== "undefined" ? window.innerWidth / 2 : 0,
        y: typeof window !== "undefined" ? window.innerHeight - 55 : 0,
        width: expectedWidth,
        height: 45,
    }));
    const formatDate = (ts) => {
        const d = new Date(ts);
        const pad = (n) => String(n).padStart(2, "0");
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}_${pad(d.getHours())}-${pad(d.getMinutes())}-${pad(d.getSeconds())}`;
    };
    const download = (chunks, name) => {
        if (!chunks.length)
            return;
        const blob = new Blob(chunks, { type: "video/webm" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${name}.webm`;
        a.click();
        URL.revokeObjectURL(url);
    };
    const pickDirectory = async () => {
        try {
            dirHandleRef.current = await window.showDirectoryPicker({
                mode: "readwrite",
            });
            return true;
        }
        catch (e) {
            console.error(e);
            return false;
        }
    };
    const saveToDir = async (chunks, name) => {
        if (!chunks.length)
            return;
        const blob = new Blob(chunks, {
            type: "video/webm",
        });
        if (!dirHandleRef.current) {
            download(chunks, name);
            return;
        }
        try {
            const fileHandle = await dirHandleRef.current.getFileHandle(`${name}.webm`, {
                create: true,
            });
            const writable = await fileHandle.createWritable();
            await writable.write(blob);
            await writable.close();
        }
        catch (e) {
            console.error(e);
            download(chunks, name);
        }
    };
    const checkSavedVideos = async () => {
        const labels = Object.keys(streams);
        for (const label of labels) {
            const keys = await getAllSegmentKeys();
            for (const key of keys) {
                const chunks = await getAllChunks(key);
                if (chunks.length > 0) {
                    setHasVideos(true);
                    return;
                }
            }
        }
        setHasVideos(false);
    };
    const start = () => {
        var _a, _b;
        (_a = frontRef.current) === null || _a === void 0 ? void 0 : _a.start();
        (_b = backRef.current) === null || _b === void 0 ? void 0 : _b.start();
        localStorage.setItem("recording_active", "1");
        setRecording(true);
        setPaused(false);
    };
    const pauseAll = async () => {
        var _a, _b;
        (_a = frontRef.current) === null || _a === void 0 ? void 0 : _a.pause();
        (_b = backRef.current) === null || _b === void 0 ? void 0 : _b.pause();
        setPaused(true);
        await checkSavedVideos();
    };
    const resumeAll = () => {
        var _a, _b;
        (_a = frontRef.current) === null || _a === void 0 ? void 0 : _a.resume();
        (_b = backRef.current) === null || _b === void 0 ? void 0 : _b.resume();
        setPaused((prev) => !prev);
    };
    const stop = async () => {
        var _a, _b;
        await Promise.all([(_a = frontRef.current) === null || _a === void 0 ? void 0 : _a.stop(), (_b = backRef.current) === null || _b === void 0 ? void 0 : _b.stop()]);
        await checkSavedVideos();
        localStorage.removeItem("recording_active");
        setRecording(false);
        setPaused(false);
    };
    const saveAllVideos = async () => {
        const picked = await pickDirectory();
        if (!picked)
            return;
        const keys = await getAllSegmentKeys();
        for (const key of keys) {
            const chunks = await getAllChunks(key);
            if (!chunks.length)
                continue;
            const meta = await getSegmentMeta(key);
            const started = (meta === null || meta === void 0 ? void 0 : meta.startedAt) ? formatDate(meta.startedAt) : "unknown";
            const stopped = (meta === null || meta === void 0 ? void 0 : meta.stoppedAt) ? formatDate(meta.stoppedAt) : "unknown";
            const filename = `${meta === null || meta === void 0 ? void 0 : meta.label}_${started}__${stopped}`;
            await saveToDir(chunks, filename);
            await clearChunks(key);
        }
        await checkSavedVideos();
    };
    useEffect(() => {
        checkSavedVideos();
    }, []);
    useEffect(() => {
        setDimensions((prev) => (Object.assign(Object.assign({}, prev), { width: expectedWidth })));
    }, [hasVideos, expectedWidth]);
    useEffect(() => {
        const handleResize = () => {
            setDimensions((prev) => (Object.assign(Object.assign({}, prev), { x: window.innerWidth / 2, y: window.innerHeight - 55 })));
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);
    return (_jsxs(_Fragment, { children: [_jsx(StreamRecorder, { ref: frontRef, label: "front", stream: streams.front, active: recording, setPaused: setPaused }), _jsx(StreamRecorder, { ref: backRef, label: "back", stream: streams.back, active: recording, setPaused: setPaused }), _jsx(Rnd, { size: { width: dimensions.width, height: dimensions.height }, position: { x: dimensions.x, y: dimensions.y }, onDragStop: (e, d) => {
                    setDimensions((prev) => (Object.assign(Object.assign({}, prev), { x: d.x, y: d.y })));
                }, onResizeStop: (e, direction, ref, delta, position) => {
                    setDimensions(Object.assign({ width: parseInt(ref.style.width, 10), height: parseInt(ref.style.height, 10) }, position));
                }, style: {
                    zIndex: "110000",
                }, children: _jsxs("div", { className: "recorder-wrapper", children: [!recording ? (_jsx("button", { className: "btn reload start-rec", onClick: start, title: "\u041F\u043E\u0447\u0430\u0442\u0438 \u0437\u0430\u043F\u0438\u0441 \u0432\u0456\u0434\u0435\u043E" })) : (_jsx("button", { className: "btn reload stop-rec", onClick: stop, disabled: !recording, title: "\u0417\u0443\u043F\u0438\u043D\u0438\u0442\u0438 \u0437\u0430\u043F\u0438\u0441 \u0432\u0456\u0434\u0435\u043E" })), recording && !paused && (_jsx("button", { className: "btn reload", onClick: pauseAll, children: _jsx("img", { src: CONTROL_ICONS.recordPause, height: "15", width: "15", alt: "pause" }) })), recording && paused && (_jsx("button", { className: "btn reload recording", onClick: resumeAll, title: "\u041F\u0440\u043E\u0434\u043E\u0432\u0436\u0438\u0442\u0438 \u0437\u0430\u043F\u0438\u0441 \u0432\u0456\u0434\u0435\u043E" })), hasVideos && !recording && (_jsx("button", { className: "btn reload", onClick: saveAllVideos, children: _jsx("img", { src: CONTROL_ICONS.recordSave, height: "20", width: "20", alt: "save" }) }))] }) })] }));
};
export default Recorder;
